"use client";

import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Footer } from "@/components/sections/premium/ManifestoSection";
import Image from "next/image";

const RevealText = ({ children }: { children: string }) => {
    const text = children.split(" ");
    return (
        <span className="inline-flex flex-wrap gap-x-2">
            {text.map((word, i) => (
                <motion.span
                    initial={{ opacity: 0.1, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-10%" }}
                    transition={{ duration: 0.6, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                    key={i}
                    className="inline-block"
                >
                    {word}
                </motion.span>
            ))}
        </span>
    );
};

export const AboutPageContent = () => {
    const philRef = useRef(null);
    const { scrollYProgress: philProgress } = useScroll({
        target: philRef,
        offset: ["start end", "end start"]
    });
    
    // Parallax values for Philosophy
    const imageY = useTransform(philProgress, [0, 1], ["-15%", "15%"]);
    const textY = useTransform(philProgress, [0, 1], ["10%", "-10%"]);

    return (
        <main className="relative bg-[#0a0a0a] text-[#f4f4f0] min-h-screen selection:bg-primary selection:text-white font-sans overflow-hidden">
            {/* COLLINS STYLE HERO */}
            <section className="relative h-screen w-full flex flex-col justify-between p-6 md:p-12 overflow-hidden bg-black">
                <div className="relative z-10 flex justify-between items-start mt-24">
                    <motion.span 
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 1, delay: 0.5 }}
                        className="font-sans text-sm md:text-base uppercase tracking-[0.4em] text-white/50"
                    >
                        THE STUDIO / IDENTITY
                    </motion.span>
                    <motion.span 
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 1, delay: 0.5 }}
                        className="font-mono text-xs md:text-sm text-white/30 text-right tracking-widest uppercase"
                    >
                        MISSION_READY <br/> [ BORN IN MUMBAI ]
                    </motion.span>
                </div>

                <div className="relative z-10 w-full flex justify-center items-center h-full">
                    <motion.h1 
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
                        className="font-display text-[15vw] md:text-[18vw] leading-[0.75] tracking-tighter uppercase text-center"
                    >
                        WE ARE <br />
                        <span className="text-transparent italic" style={{ WebkitTextStroke: '2px #f4f4f0' }}>DOTSLASHLAB.</span>
                    </motion.h1>
                </div>
            </section>

            {/* STUDIO STORY */}
            <section className="py-32 px-4 relative z-10 bg-[#0a0a0a] border-t border-white/5">
                <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-20">
                    <div className="lg:col-span-5 space-y-12">
                        <span className="font-mono text-xs text-primary uppercase tracking-[0.3em] block mb-6">/ Studio Story</span>
                        <div className="relative aspect-[3/4] w-full bg-white/5 rounded-[2rem] overflow-hidden group border border-white/10">
                            <Image
                                src="/about1.webp"
                                alt="DotSlashLab Workshop"
                                fill
                                className="absolute inset-0 w-full h-full object-cover grayscale opacity-80 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-700 scale-105 group-hover:scale-100"
                                sizes="(max-width: 768px) 100vw, 600px"
                            />
                        </div>
                    </div>
                    <div className="lg:col-span-7 space-y-16 flex flex-col justify-center">
                        <motion.h2
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                            className="font-display text-6xl md:text-8xl lg:text-[7vw] uppercase leading-[0.85] tracking-tighter"
                        >
                            We don’t build <br /> <span className="text-white/30 italic">MVPs.</span> <br />
                            We build <span className="text-primary italic">real products.</span>
                        </motion.h2>
                        <div className="space-y-12 max-w-3xl border-t border-white/10 pt-12">
                            <p className="font-sans text-3xl md:text-5xl text-white/90 leading-tight uppercase tracking-tight italic font-light">
                                <RevealText>
                                    DotSlashLab is a high-velocity product lab. We turn strategic vision into production-ready reality. No bloated teams. No wasted cycles. Just absolute execution.
                                </RevealText>
                            </p>
                            <p className="font-sans text-xl md:text-2xl text-white/50 leading-relaxed uppercase tracking-tight">
                                Execution is where most ideas fail. We exist to fix that. Our process is designed for founders who move fast and care about direction.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* HIGH CONTRAST PHILOSOPHY BLOCK WITH PARALLAX */}
            <section ref={philRef} className="pt-32 pb-32 px-4 relative z-10">
                <div className="max-w-[1600px] mx-auto">
                    <div className="bg-[#f4f4f0] text-[#0a0a0a] p-8 md:p-16 lg:p-32 rounded-[2rem] shadow-2xl grid grid-cols-1 lg:grid-cols-12 gap-20 overflow-hidden relative">
                        {/* Subtle noise on the light block */}
                        <div className="absolute inset-0 bg-noise opacity-[0.03] mix-blend-multiply pointer-events-none" />

                        <div className="lg:col-span-5 relative z-10">
                            <span className="font-mono text-xs text-black/40 uppercase tracking-[0.3em] block mb-12">/ Philosophy</span>
                            <div className="relative aspect-[3/4] w-full rounded-2xl overflow-hidden border border-black/10">
                                <motion.div style={{ y: imageY }} className="absolute inset-[-15%] w-[130%] h-[130%]">
                                    <Image
                                        src="/about2.webp"
                                        alt="Philosophy Visual"
                                        fill
                                        className="absolute inset-0 w-full h-full object-cover grayscale brightness-90 contrast-125"
                                        sizes="(max-width: 768px) 100vw, 400px"
                                    />
                                </motion.div>
                            </div>
                        </div>
                        <div className="lg:col-span-7 flex flex-col justify-center relative z-10">
                            <motion.div style={{ y: textY }} className="space-y-16">
                                <div className="space-y-8">
                                    <span className="font-mono text-xs text-primary tracking-widest uppercase block">[ CORE_VAL_01 ]</span>
                                    <h3 className="font-display text-7xl md:text-[8vw] uppercase leading-[0.8] tracking-tighter italic text-black">
                                        Execution <br/> <span className="text-transparent" style={{ WebkitTextStroke: '2px #0a0a0a' }}>over noise.</span>
                                    </h3>
                                    <p className="font-sans text-3xl md:text-5xl text-black/80 max-w-2xl uppercase tracking-tighter leading-none mt-8">
                                        Speed is the only real edge. We ship fast, iterate hard, and never compromise on production quality.
                                    </p>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-12 pt-12 border-t border-black/20">
                                    <div className="space-y-4">
                                        <span className="font-display text-4xl md:text-5xl uppercase tracking-tighter text-black">Built with intent.</span>
                                        <p className="font-sans text-xl text-black/60 uppercase tracking-tight leading-snug">
                                            No fluff. No over-engineering. Just high-impact products that solve real problems.
                                        </p>
                                    </div>
                                    <div className="space-y-4">
                                        <span className="font-display text-4xl md:text-5xl uppercase tracking-tighter text-black">Products that ship.</span>
                                        <p className="font-sans text-xl text-black/60 uppercase tracking-tight leading-snug">
                                            We work with founders who trust our vision and our ability to deliver at speed.
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </section>            

            {/* THE OPERATORS - CINEMATIC CARDS */}
            <section className="pt-32 pb-40 px-4 bg-[#0a0a0a] text-[#f4f4f0] relative z-10 border-t border-white/5">
                <div className="max-w-[1600px] mx-auto">
                    <div className="space-y-8 mb-24 text-center">
                        <span className="font-mono text-xs text-primary uppercase tracking-[0.3em] block">/ Mission Control</span>
                        <h2 className="font-display text-[12vw] md:text-[10vw] uppercase leading-[0.8] tracking-tighter">
                            THE <br /> <span className="italic text-transparent" style={{ WebkitTextStroke: '2px #f4f4f0' }}>OPERATORS.</span>
                        </h2>
                    </div>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
                        {/* Aaditya Singh */}
                        <div className="group relative">
                            <div className="absolute inset-0 bg-white/5 rounded-3xl -m-8 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                            <div className="flex flex-col space-y-8 relative z-10">
                                <div className="border-b border-white/10 pb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-6">
                                    <div>
                                        <span className="font-mono text-xs text-white/30 tracking-[0.2em] uppercase block mb-4">[ OP_ID: 01 ]</span>
                                        <h3 className="font-display text-6xl md:text-7xl uppercase tracking-tighter">Aadiitya Singh</h3>
                                        <p className="font-sans text-2xl text-primary uppercase tracking-widest italic mt-2">Founder / Vision & Command</p>
                                    </div>
                                    <div className="flex gap-4">
                                        <a href="https://x.com/aadiityastwt" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center hover:bg-white hover:text-black transition-colors">
                                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
                                        </a>
                                        <a href="https://www.linkedin.com/in/aadityasingh18/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center hover:bg-white hover:text-black transition-colors">
                                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
                                        </a>
                                    </div>
                                </div>
                                <p className="font-sans text-3xl text-white/60 uppercase tracking-tight leading-snug">
                                    Architect of high-velocity systems and the mind behind <a href="https://www.instagram.com/99byte.dev/" target="_blank" rel="noopener noreferrer" className="text-white hover:text-primary transition-colors border-b border-white/30">@99byte.dev</a>. Aaditya brings extensive technical obsession to scaling digital products.
                                </p>
                            </div>
                        </div>

                        {/* Manoj Singh */}
                        <div className="group relative">
                            <div className="absolute inset-0 bg-white/5 rounded-3xl -m-8 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                            <div className="flex flex-col space-y-8 relative z-10">
                                <div className="border-b border-white/10 pb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-6">
                                    <div>
                                        <span className="font-mono text-xs text-white/30 tracking-[0.2em] uppercase block mb-4">[ OP_ID: 02 ]</span>
                                        <h3 className="font-display text-6xl md:text-7xl uppercase tracking-tighter">Manoj Singh</h3>
                                        <p className="font-sans text-2xl text-primary uppercase tracking-widest italic mt-2">Co-Founder / System Backbone</p>
                                    </div>
                                    <div className="flex gap-4">
                                        <a href="https://x.com/manojstwt" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center hover:bg-white hover:text-black transition-colors">
                                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
                                        </a>
                                        <a href="https://www.linkedin.com/in/manoj-singh-655a39110/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center hover:bg-white hover:text-black transition-colors">
                                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
                                        </a>
                                    </div>
                                </div>
                                <p className="font-sans text-3xl text-white/60 uppercase tracking-tight leading-snug">
                                    Co-Founder driving product strategy, operations, and scaling. Manoj orchestrates tactical execution across the studio, transforming vision into robust market solutions.
                                </p>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            <Footer />

            {/* Background premium detail */}
            <div className="bg-noise opacity-10 pointer-events-none fixed inset-0 z-50 mix-blend-overlay" />
        </main>
    );
};
